import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, Mail, CheckCircle, AlertCircle, Loader2, Edit,
  Bell, Banknote, Hash, Clock
} from 'lucide-react';
import { toast } from 'sonner';

// Triggers in the order they should render. Matches the four backend constants.
const TRIGGERS = [
  {
    key:         'registration_confirmed',
    title:       'Registration Confirmed',
    description: 'Sent after a registrant pays online (Razorpay / Cashfree / Instamojo) or registers for a free event.',
    Icon:        CheckCircle,
    color:       'text-green-600',
    bg:          'bg-green-50',
    border:      'border-green-200'
  },
  {
    key:         'payment_received',
    title:       'Payment Received',
    description: 'Sent after a volunteer marks cash as collected at the venue (offline / kiosk).',
    Icon:        Banknote,
    color:       'text-emerald-600',
    bg:          'bg-emerald-50',
    border:      'border-emerald-200'
  },
  {
    key:         'payment_otp',
    title:       'Payment OTP',
    description: 'Sent immediately after a registrant chooses "Pay at Venue". Contains the 6-digit OTP.',
    Icon:        Hash,
    color:       'text-amber-600',
    bg:          'bg-amber-50',
    border:      'border-amber-200'
  },
  {
    key:         'registration_reminder',
    title:       'Registration Reminder',
    description: 'Sent when an admin clicks "Send Reminders" on the Members page (closed-group campaigns).',
    Icon:        Bell,
    color:       'text-blue-600',
    bg:          'bg-blue-50',
    border:      'border-blue-200'
  }
];

const EmailTemplates = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [campRes, tplRes] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get(`/campaigns/${campaignId}/email-templates`)
      ]);
      setCampaign(campRes.data);
      setTemplates(tplRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Map: trigger → template object (in case API returned them in any order)
  const byTrigger = templates.reduce((acc, t) => {
    acc[t.trigger] = t;
    return acc;
  }, {});

  const isComplete = (t) => t && t.subject?.trim() && t.body_html?.trim();
  const incompleteCount = TRIGGERS.filter((trg) => !isComplete(byTrigger[trg.key])).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(`/campaigns/${campaignId}`)}
              className="mb-3 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Event
            </Button>
            <h1 className="text-2xl font-outfit font-bold text-slate-900">Email Templates</h1>
            <p className="text-sm text-slate-600 font-jakarta mt-1">
              {campaign?.name} — customize the four transactional emails sent to registrants.
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Status</p>
            {incompleteCount === 0 ? (
              <p className="text-sm font-semibold text-green-600 flex items-center gap-1 mt-1">
                <CheckCircle className="w-4 h-4" />
                All 4 templates ready
              </p>
            ) : (
              <p className="text-sm font-semibold text-amber-600 flex items-center gap-1 mt-1">
                <AlertCircle className="w-4 h-4" />
                {incompleteCount} template{incompleteCount !== 1 ? 's' : ''} incomplete
              </p>
            )}
          </div>
        </div>

        {/* Activation warning banner */}
        {incompleteCount > 0 && (
          <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-900">
                  Templates required before activating this event
                </p>
                <p className="text-sm text-amber-800 mt-1">
                  Each of the four templates below must have a subject and body filled in.
                  Open the incomplete ones (highlighted) and click <strong>Save</strong>.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Helpful intro */}
        <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p>
                Each card below represents one email that this event sends automatically.
                Templates are pre-populated with defaults — you can edit subject, body, CC,
                BCC, and add images / dynamic field values.
              </p>
              <p className="mt-2">
                <strong>Placeholders</strong> like <code className="bg-blue-100 px-1 rounded">{'${name}'}</code>,{' '}
                <code className="bg-blue-100 px-1 rounded">{'${amount}'}</code>, and{' '}
                <code className="bg-blue-100 px-1 rounded">{'${field:Email}'}</code> are substituted
                with real values before sending. Use the placeholder picker in the editor to insert them.
              </p>
            </div>
          </div>
        </Card>

        {/* Template cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TRIGGERS.map((trg) => {
            const tpl = byTrigger[trg.key];
            const complete = isComplete(tpl);
            const { Icon } = trg;
            return (
              <Card
                key={trg.key}
                className={`p-5 transition-all hover:shadow-md cursor-pointer ${
                  complete ? 'border-slate-200' : `${trg.border} ring-1 ring-amber-200`
                }`}
                onClick={() =>
                  navigate(`/campaigns/${campaignId}/email-templates/${trg.key}`)
                }
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2 rounded-lg ${trg.bg}`}>
                    <Icon className={`w-5 h-5 ${trg.color}`} />
                  </div>
                  {complete ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      Ready
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                      <AlertCircle className="w-3 h-3" />
                      Incomplete
                    </span>
                  )}
                </div>
                <h3 className="font-outfit font-semibold text-slate-900 text-lg">{trg.title}</h3>
                <p className="text-xs text-slate-500 font-jakarta mt-1 mb-3">{trg.description}</p>

                <div className="text-xs text-slate-600 font-jakarta border-t border-slate-100 pt-3 space-y-1">
                  <div className="flex">
                    <span className="text-slate-400 w-16">Subject:</span>
                    <span className="font-medium truncate">{tpl?.subject || '— Not set —'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-slate-400 w-16">CC:</span>
                    <span>{tpl?.cc?.length > 0 ? `${tpl.cc.length} recipient${tpl.cc.length !== 1 ? 's' : ''}` : 'None'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 w-16">Last edited:</span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <Clock className="w-3 h-3" />
                      {tpl?.updated_at ? new Date(tpl.updated_at).toLocaleDateString('en-IN') : '—'}
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/campaigns/${campaignId}/email-templates/${trg.key}`);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Template
                </Button>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplates;
